import type { SkillCatalogEntry } from "@/types/skills";

import { useCallback, useEffect, useState } from "react";

import { logger } from "@/telemetry";
import { siteConfig } from "@/config/site";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";

/**
 * Minimal shape of a skill needed to surface it as a selectable row in the
 * chat `@s` mention menu (and the `/` command menu). Mirrors the relevant
 * fields of the admin skills catalogue (`SkillCatalogEntry`).
 */
export interface SkillListItem {
  id: string;
  name: string;
  description?: string;
}

/**
 * Module-level cache so navigating between chats shows the previous list
 * immediately instead of flashing empty while the refetch runs.
 *
 * `cachedForClient` ties the cache to the API client instance it was fetched
 * with. If a different user signs in within the same tab (new client instance),
 * the cache is treated as stale so one user's skill names never leak into
 * another's initial render.
 */
let skillCache: SkillListItem[] | null = null;
let cachedForClient: unknown = null;
/** Epoch ms of the last successful fetch; 0 = never fetched. */
let cachedAt = 0;

/**
 * Mounted hook instances subscribe here so a cache invalidation (e.g. a skill
 * created or toggled in Settings) refetches the list everywhere it is shown,
 * without waiting for a remount. The composer stays mounted across "new chat",
 * so a mount-only fetch goes stale the moment a skill is created.
 */
/**
 * A subscriber is notified when the shared cache changes. `next === null` means
 * the cache was invalidated and the consumer should refetch; a non-null list
 * means a fresh fetch just completed and the consumer should adopt it directly.
 * Adopting (rather than each consumer refetching) is what lets a refresh in one
 * mounted consumer propagate to the others without an N-way refetch storm, and
 * without the infinite loop a refetch-on-every-success would cause.
 */
type SkillListSubscriber = (next: SkillListItem[] | null) => void;
const subscribers = new Set<SkillListSubscriber>();

/**
 * A list older than this is refetched when the caller signals intent to use it
 * (the `@`/`/` menus opening). Covers skills created outside this tab's
 * Settings page, e.g. by the skill-creator agent from another chat.
 */
const STALE_AFTER_MS = 30_000;

/** Drop the cached skill list. Call on logout to avoid cross-session leakage. */
export const clearSkillListCache = (): void => {
  skillCache = null;
  cachedForClient = null;
  cachedAt = 0;
};

/**
 * Drop the cache AND refetch in every mounted consumer. Call after any
 * operation that changes the catalogue (create, delete, toggle, access
 * change), so the chat `@s` and `/` menus reflect it immediately.
 */
export const invalidateSkillListCache = (): void => {
  clearSkillListCache();
  subscribers.forEach((notify) => notify(null));
};

/**
 * useSkillList
 *
 * Fetches the skills the current user can use via the admin skills catalogue
 * (`GET /admin/v1/skills/catalog`). The backend already scopes the catalogue to
 * the user's access, and we additionally surface only global or active skills —
 * the assistant cannot use a disabled tenant skill. The returned list doubles
 * as the visibility gate for the `@s` mention section and `/` command menu.
 *
 * Defensive by design: if the API client (or `authenticatedFetch`) is
 * unavailable, or the request fails, the hook resolves to an empty list rather
 * than throwing. This keeps it safe in tests where the client is mocked.
 */
export const useSkillList = (): {
  skills: SkillListItem[];
  isLoading: boolean;
  refreshIfStale: () => void;
} => {
  const { authenticatedFetch } = useWebuildAPIClient();
  // Only seed from cache when it was fetched with the current client instance.
  const [skills, setSkills] = useState<SkillListItem[]>(() =>
    cachedForClient === authenticatedFetch ? (skillCache ?? []) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  // Bumped by invalidateSkillListCache (via the subscription below) and by
  // refreshIfStale to re-run the fetch effect without a remount.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    // null => cache invalidated, refetch; a list => adopt the fresh data
    // another consumer just fetched, without triggering our own refetch.
    const notify: SkillListSubscriber = (next) => {
      if (next === null) {
        setRefreshTick((tick) => tick + 1);
      } else {
        setSkills(next);
      }
    };

    subscribers.add(notify);

    return () => {
      subscribers.delete(notify);
    };
  }, []);

  /**
   * Refetch when the cached list is older than STALE_AFTER_MS. Call when the
   * user signals intent to pick a skill (opening the `@` or `/` menus), so
   * skills created elsewhere (another tab, the skill-creator agent in chat)
   * appear without a page refresh.
   */
  const refreshIfStale = useCallback(() => {
    if (Date.now() - cachedAt > STALE_AFTER_MS) {
      setRefreshTick((tick) => tick + 1);
    }
  }, []);

  useEffect(() => {
    if (typeof authenticatedFetch !== "function") return;

    let cancelled = false;

    setIsLoading(true);

    const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");

    authenticatedFetch(`${baseUrl}/admin/v1/skills/catalog`)
      .then(async (response) => {
        if (!response.ok) {
          logger.error("useSkillList: failed to fetch skills catalogue", {
            status: response.status,
          });

          return;
        }

        const data: SkillCatalogEntry[] = await response.json();

        if (cancelled) return;

        const mapped: SkillListItem[] = (data ?? [])
          .filter((s) => s.is_global || s.is_active)
          .map((s) => ({
            id: s.id,
            name: s.name,
            description: s.description || undefined,
          }));

        skillCache = mapped;
        cachedForClient = authenticatedFetch;
        cachedAt = Date.now();
        // Push to every mounted consumer (including this one) so a refresh in
        // one propagates to the others; adopting avoids an N-way refetch storm.
        subscribers.forEach((notify) => notify(mapped));
      })
      .catch((error: unknown) => {
        logger.error("useSkillList: error fetching skills catalogue", { error: String(error) });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authenticatedFetch, refreshTick]);

  return { skills, isLoading, refreshIfStale };
};
